<template>
  <full-screen :title="t('menu-availability.title')" @close="router.back()">
    <div
      class="w-full h-full md:w-3/5 px-4 md:px-0 mx-auto pt-4 flex flex-col flex-1 overflow-hidden"
    >
      <div class="flex flex-row w-full gap-2 flex-none">
        <l-search-bar
          v-model="searchQuery"
          class="w-2/3"
          icon="search"
          size="medium"
          :placeholder="t('menu-availability.search')"
        />
        <div class="w-1/3">
          <l-select v-model="activeStateFilter" :options="productFilterOptions" size="medium" />
        </div>
        <l-button
          v-if="hasStockEnabled"
          icon="printer"
          type="primary"
          class="flex-none text-sm"
          @click="printReport"
          >{{ t('menu-availability.stock.print') }}</l-button
        >
      </div>
      <div class="overflow-x-auto scrolling-touch shrink-0 mt-4">
        <l-tab-list v-model="activeTypeFilter" :tabs="tabs" :extend-line="true" />
      </div>
      <template v-if="!isLoading">
        <template v-if="hasResults">
          <div class="flex flex-col flex-1 overflow-y-auto relative py-4">
            <transition-group name="results-list">
              <template v-for="tab in tabs.slice(1)" :key="tab.id">
                <div
                  v-if="
                    (activeTypeFilter === 'all' || activeTypeFilter === tab.id) &&
                    filteredStockData[tab.id as StockItemTypes].length > 0
                  "
                  class="mb-4"
                >
                  <div v-if="activeTypeFilter === 'all'" class="text-base text-n-10 font-medium">
                    {{ tab.name }}
                  </div>

                  <div
                    :class="[
                      'grid gap-x-4 px-4 mb-3 mt-4 text-n-0 text-sm',
                      hasStockEnabled && tab.id === 'product'
                        ? 'grid-cols-[2fr_1fr_1fr_1fr_1fr_2.25rem]'
                        : 'grid-cols-[1fr_auto]'
                    ]"
                  >
                    <template v-if="hasStockEnabled && tab.id === 'product'">
                      <div class="hidden md:contents">
                        <div>{{ t('menu-availability.stock.product-label') }}</div>
                        <div class="text-center">
                          {{ t('menu-availability.stock.initial-stock') }}
                        </div>
                        <div class="text-center">
                          {{ t('menu-availability.stock.stock-reset') }}
                        </div>
                        <div class="text-center">
                          {{ t('menu-availability.stock.current-stock') }}
                        </div>
                        <div class="text-center">{{ t('menu-availability.stock.to-restock') }}</div>
                      </div>
                    </template>
                  </div>
                  <transition-group
                    name="results-list"
                    class="grid grid-cols-1 sm:grid-cols-1 gap-4 mt-4"
                    tag="div"
                    @before-leave="beforeLeave"
                  >
                    <stock-list-item
                      v-for="item of filteredStockData[tab.id as StockItemTypes]"
                      :key="item.id"
                      :item="item"
                      :show-stock="!!hasStockEnabled"
                    />
                  </transition-group>
                </div>
                <div
                  v-else-if="
                    activeTypeFilter === tab.id &&
                    filteredStockData[tab.id as StockItemTypes].length === 0
                  "
                  class="mt-4 flex flex-1 items-center"
                >
                  <l-empty-case class="flex-1" type="empty">
                    {{ t('search.no-results') }}
                  </l-empty-case>
                </div>
              </template>
            </transition-group>
          </div>
        </template>
        <div v-else class="mt-4 flex flex-1 items-center">
          <l-empty-case class="flex-1" type="empty">
            {{ t('search.no-results') }}
          </l-empty-case>
        </div>
      </template>
    </div>
  </full-screen>
</template>

<script setup lang="ts">
import { computedAsync } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { LButton, LEmptyCase, LSearchBar, LSelect, LTabList } from '@last/core-ui/paprika'

import FullScreen from '@/components/FullScreen.vue'
import TicketPrinter from '@/integrations/printer/ticketPrinter'
import { useConfigStore } from '@/store/config'
import { useStockStore } from '@/store/stock'
import type { StockItemTypes } from '@/types'

import StockListItem from './StockListItem.vue'

const { t } = useI18n()
const router = useRouter()

const stockStore = useStockStore()
const {
  isLoading,
  hasResults,
  activeTypeFilter,
  activeStateFilter,
  searchQuery,
  filteredStockData
} = storeToRefs(stockStore)

const configStore = useConfigStore()
const { config } = storeToRefs(configStore)

stockStore.fetchStock()

const hasStockEnabled = computedAsync(() => {
  return config.value.lastProducts.stock
})

const productFilterOptions = [
  { label: t('menu-availability.filter-by-all'), value: 'ALL' },
  { label: t('menu-availability.filter-by-enabled'), value: 'ENABLED' },
  { label: t('menu-availability.filter-by-disabled'), value: 'DISABLED' }
]

const tabs: { name: string; id: StockItemTypes | 'all' }[] = [
  {
    name: t('menu-availability.filter-by-all'),
    id: 'all'
  },
  {
    name: t('menu-availability.products'),
    id: 'product'
  },
  {
    name: t('menu-availability.combos'),
    id: 'combo'
  },
  {
    name: t('menu-availability.modifiers'),
    id: 'modifier'
  },
  {
    name: t('menu-availability.modifier-groups'),
    id: 'modifierGroup'
  }
]

function beforeLeave(element: Element) {
  const el = element as HTMLElement
  const { marginLeft, marginTop, width, height } = window.getComputedStyle(el)

  el.style.left = `${el.offsetLeft - parseFloat(marginLeft)}px`
  el.style.top = `${el.offsetTop - parseFloat(marginTop)}px`
  el.style.width = width
  el.style.height = height
}

function printReport() {
  TicketPrinter.printStockReport(filteredStockData.value.product)
}
</script>

<style scoped>
.results-list-enter-from {
  opacity: 0;
  transform: scale(0.6);
}
.results-list-enter-active {
  transition: all 0.4s ease;
}
.results-list-leave-to {
  opacity: 0;
  transform: scale(0.6);
}
.results-list-leave-active {
  transition: all 0.3s ease;
  position: absolute;
}
.results-list-move {
  transition: all 0.3s ease;
}
</style>
